import React, { useEffect, useState, useCallback } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

/**
 * @typedef {Object} PlaceResult
 * @property {Object} geometry
 * @property {string} name
 * @property {string} formatted_address
 */

/**
 * @param {{ onPlaceSelect: (place: PlaceResult | null) => void }} props
 */
export const AutocompleteCustom = ({ onPlaceSelect }) => {
  const map = useMap();
  const places = useMapsLibrary('places');

  // Gestion de l'état
  const [sessionToken, setSessionToken] = useState(null);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [predictionResults, setPredictionResults] = useState([]);
  const [inputValue, setInputValue] = useState('');

  // Initialisation des services de Google Places
  useEffect(() => {
    if (!places || !map) return;

    setAutocompleteService(new places.AutocompleteService());
    setPlacesService(new places.PlacesService(map));
    setSessionToken(new places.AutocompleteSessionToken());

    return () => setAutocompleteService(null);
  }, [map, places]);

  // Récupérer les prédictions
  const fetchPredictions = useCallback(
    async (inputValue) => {
      if (!autocompleteService || !inputValue) {
        setPredictionResults([]);
        return;
      }

      const request = { input: inputValue, sessionToken };
      const response = await autocompleteService.getPlacePredictions(request);

      setPredictionResults(response.predictions);
    },
    [autocompleteService, sessionToken]
  );

  // Gestion du changement dans l'input
  const onInputChange = useCallback(
    (event) => {
      const value = event.target.value;

      setInputValue(value);
      fetchPredictions(value);
    },
    [fetchPredictions]
  );

  // Gestion des clics sur les suggestions
  const handleSuggestionClick = useCallback(
    (placeId) => {
      if (!places) return;

      const detailRequestOptions = {
        placeId,
        fields: ['geometry', 'name', 'formatted_address'],
        sessionToken,
      };

      const detailsRequestCallback = (placeDetails) => {
        onPlaceSelect(placeDetails);
        setPredictionResults([]);
        setInputValue(placeDetails?.formatted_address ?? '');
        setSessionToken(new places.AutocompleteSessionToken());
      };

      placesService?.getDetails(detailRequestOptions, detailsRequestCallback);
    },
    [onPlaceSelect, places, placesService, sessionToken]
  );

  // Rendu du composant
  return (
    <>
      <input
        type="text"
        name="address"
        value={inputValue}
        onInput={(event) => onInputChange(event)}
        placeholder="Adresse complète (optionnel)"
      />

      {predictionResults.length > 0 && (
        <ul className="custom-list">
          {predictionResults.map(({ place_id, description }) => {
            return (
              <li
                key={place_id}
                className="custom-list-item"
                onClick={() => handleSuggestionClick(place_id)}
                style={{paddingTop: '10px', fontSize: 'medium'}}
              >
                {description}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};